import { useApolloClient, useMutation } from "@apollo/client/react";
import { flushSync } from "react-dom";

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  styled,
  TextField,
  Tooltip,
} from "@mui/material";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import Input, { inputClasses } from "@mui/material/Input";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { FileIcon as FilePhosphorIcon } from "@phosphor-icons/react/dist/csr/File";
import { FlaskIcon } from "@phosphor-icons/react/dist/csr/Flask";
import { FloppyDiskIcon } from "@phosphor-icons/react/dist/csr/FloppyDisk";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { StarIcon } from "@phosphor-icons/react/dist/csr/Star";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import * as Sentry from "@sentry/react";
import React, { ChangeEvent, useCallback, useEffect, useRef, useState, type FocusEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import DraftStatus from "../../constants/draftStatus";
import Permissions from "../../constants/permissions";
import { useAccountContext } from "../../strict/app/AccountResolver";
import useAuth from "../../context/UserContext";
import type {
  CompilationMessage,
  FlowSummary,
  UpdateChangelistMutation,
  UpdateChangelistMutationVariables,
} from "../../graphql/types";
import { RenameChangelistDocument, SaveFileInput, UpdateChangelistDocument } from "../../graphql/types";
import { parseGQLResponseError } from "../../graphql/util";
import { getOSType, OS_TYPES } from "../../lib/Util";
import { selectTestableDeclarations } from "../../store/editor/editorStore";
import { useEditorStoreContext } from "../../store/editor/EditorStoreProvider";
import type { File } from "../../store/editor/types";
import { LaunchTestDialog } from "../LaunchTestDialog";
import { hasPermission, IfPermission } from "../Permissions";
import { AccentBadge } from "../ui/badges";
import { DocsButton, StatefulButton } from "../ui/muiButtons";
import { toErrorsWarnings, ViewTestRunsButton } from "./EditorCommon";

const DraftDescriptionInput = styled(Input)(({ theme }) => ({
  border: "none",
  flex: 1,
  paddingLeft: theme.spacing(1),
  [`& .${inputClasses.disabled}`]: {
    WebkitTextFillColor: theme.palette.text50.main, // remove disabled styling from MUI
  },
}));

const PublishedIcon = styled(StarIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.accentWarning30.main,
}));

const HistoryIconStyled = styled(ClockCounterClockwiseIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const FileIcon = styled(FilePhosphorIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

type EditorAction = "save" | "proof" | "publish" | "saveBeforeProof" | null;

const onSaveChangelistError = (error: unknown, refetch?: () => void) => {
  const msg = parseGQLResponseError(error);
  if (msg) {
    toast.error(msg);
  } else {
    toast.error("There was a problem saving the Draft");
  }

  if (msg === "Cannot act on previously published changelist") {
    refetch?.();
  }
};

const showPersistSuccess = (action: EditorAction, hasWarnings = false) => {
  const suffix = hasWarnings ? " with some warnings." : "";
  const toastFn = hasWarnings ? toast.warn : toast.success;
  if (action === "proof") {
    toastFn(`Draft Saved and Tests Started${suffix}`);
  } else if (action === "publish") {
    toastFn(`Draft Published${suffix}`);
  } else {
    toastFn(`Draft Saved${suffix}`);
  }
};

const showValidationFailure = (action: EditorAction) => {
  if (action === "saveBeforeProof" || action === "proof") {
    toast.error("Cannot initiate Test - Fix Validation Errors.");
  } else if (action === "publish") {
    toast.error("Cannot Publish - Fix Validation Errors.");
  } else if (action === "save") {
    toast.error("Fix Validation Errors.");
  } else {
    toast.error("Cannot Save");
  }
};

const showToastMessage = (action: EditorAction, messages: Array<CompilationMessage>, canSeeBetaMessages: boolean) => {
  const { error, warning, beta } = toErrorsWarnings(messages);
  if (error === 0) {
    showPersistSuccess(action, warning > 0 || (beta > 0 && canSeeBetaMessages));
  } else {
    showValidationFailure(action);
  }
};

const createPayload = (files: File[]) => {
  const allUpdated = files.filter(
    (file) => !file.editFlags?.deleted && (file.editFlags?.created || file.editFlags?.updated)
  );
  const filesPayload = allUpdated.reduce<Record<string, SaveFileInput>>((p, file) => {
    if (file.path) {
      p[file.path] = { path: file.path, text: file.text };
    }
    return p;
  }, {});

  const renamed = files.filter((file) => !file.editFlags?.deleted && file.editFlags?.renamed);
  // Only add renamed files that weren't updated or created
  renamed.forEach((file) => {
    if (file.path && !(file.path in filesPayload)) {
      filesPayload[file.path] = {
        path: file.path,
        original_path: file.editFlags?.originalPath,
      };
    }
  });

  const renamedAndUpdated = files.filter(
    (file) =>
      !file.editFlags?.deleted && file.editFlags?.renamed && (file.editFlags?.created || file.editFlags?.updated)
  );
  // For renamed and updated files, remove the text from the original file in the files payload
  renamedAndUpdated.forEach((file) => {
    if (file.editFlags?.originalPath && !(file.editFlags.originalPath in filesPayload)) {
      filesPayload[file.editFlags.originalPath] = {
        path: file.editFlags.originalPath,
        text: null,
      };
    }
  });

  const deletedOnServer = files.filter((file) => file.editFlags?.deleted && !file.editFlags?.created);
  // For deleted files, remove the text from the file.
  deletedOnServer.forEach((file) => {
    if (file.editFlags?.originalPath && !(file.editFlags.originalPath in filesPayload)) {
      filesPayload[file.editFlags.originalPath] = {
        path: file.editFlags.originalPath,
        text: null,
      };
    }
  });

  const hasClientOnlyDeletions = files.some((f) => f.editFlags?.deleted && f.editFlags?.created);

  return {
    payload: { files: Object.values(filesPayload) },
    hasClientOnlyDeletions,
  };
};

const getTooltip = (button: "save" | "test" | "publish") => {
  const keys =
    getOSType() === OS_TYPES.Mac
      ? { ctrl: <kbd>&#x2318;</kbd>, enter: <kbd>return</kbd> }
      : { ctrl: <kbd>Ctrl</kbd>, enter: <kbd>enter</kbd> };
  if (button === "save") {
    return (
      <div>
        {keys.ctrl} + <kbd>S</kbd>
      </div>
    );
  } else if (button === "test") {
    return (
      <div>
        {keys.ctrl} + <kbd>Y</kbd>
      </div>
    );
  } else {
    return (
      <div>
        {keys.ctrl} + {keys.enter}
      </div>
    );
  }
};

const UnsavedChangesDialog = ({
  open,
  onClose,
  onConfirm,
  setExitPath,
}: DialogProps & {
  onConfirm: () => void;
  setExitPath: (path: string) => void;
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle variant="heading.four" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <WarningIcon color="warning" fontSize="large" />
      Unsaved Changes
    </DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ fontSize: 14 }}>
        You have unsaved changes. Are you sure you want to leave this page?
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button
        onClick={(e) => {
          if (onClose) {
            onClose(e, "escapeKeyDown");
          }
          setExitPath("");
        }}
        variant="outlined"
      >
        Cancel
      </Button>
      <Button onClick={onConfirm} variant="contained" color="error">
        Leave
      </Button>
    </DialogActions>
  </Dialog>
);

const MergeWarningDialog = ({
  open,
  onClose,
  onConfirm,
}: DialogProps & {
  onConfirm: () => void;
}) => {
  const [disabled, setDisabled] = useState(true);

  const onClickConfirm = () => {
    setDisabled(true);
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle variant="heading.four" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningIcon color="warning" fontSize="large" />
        Possible Merge Conflict
      </DialogTitle>
      <DialogContent>
        <>
          <DialogContentText sx={{ fontSize: 14 }}>
            This draft was branched from an <b>older</b> version than the current
            <b> Published</b> version. Publishing this draft may <b>overwrite</b> current published changes.
            <br />
            In order to confirm the changes, please type <b>overwrite</b> below.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="overwrite"
            onChange={(e) => setDisabled(e.target.value !== "overwrite")}
            slotProps={{ htmlInput: { role: "input" } }}
            fullWidth
            variant="standard"
          />
        </>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={(e) => onClose && onClose(e, "escapeKeyDown")}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" onClick={onClickConfirm} disabled={disabled}>
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DraftDescription = ({ description }: { description: string }) => {
  const descriptionValue = useRef(description);
  const [value, setValue] = useState(description);
  const inputRef = useRef<HTMLInputElement>(null);
  const readOnly = useEditorStoreContext((store) => [DraftStatus.RUNNING, DraftStatus.REPLACED].includes(store.status));
  const status = useEditorStoreContext((store) => store.status);
  const changelistId = useEditorStoreContext((store) => store.id);
  const { user } = useAuth();

  const canEdit = hasPermission(Permissions.DevelopScripts, user);

  let startIcon: React.ReactNode | null = null;

  if (status === DraftStatus.RUNNING) {
    startIcon = <PublishedIcon fontSize="large" />;
  } else if (status === DraftStatus.REPLACED) {
    startIcon = <HistoryIconStyled fontSize="large" />;
  } else {
    startIcon = <FileIcon fontSize="large" />;
  }

  useEffect(() => {
    const inputNode = inputRef?.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") inputNode?.blur();
      if (e.key === "Escape") {
        if (value !== descriptionValue.current) {
          flushSync(() => {
            setValue(descriptionValue.current);
          });
        }
        inputNode?.blur();
      }
    };

    if (inputNode) {
      inputNode.addEventListener("keydown", handleKeyDown);

      return () => {
        inputNode.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [inputRef, value, descriptionValue]);

  const [updateDraftDescription, { loading }] = useMutation(RenameChangelistDocument, {
    onError: (error) => {
      const message = parseGQLResponseError(error);
      if (message) toast.error(message);
      else toast.error("There was a problem renaming the draft");
    },
    onCompleted: () => {
      toast.success("Draft renamed");
    },
  });

  return (
    <Stack direction="row" spacing={1} sx={{ flexGrow: 1, alignItems: "center" }}>
      <DraftDescriptionInput
        inputRef={inputRef}
        disabled={readOnly || loading || !canEdit}
        startAdornment={startIcon}
        value={value}
        data-testid="draft-description"
        disableUnderline
        onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
        onFocus={(e: FocusEvent<HTMLInputElement>) =>
          e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)
        }
        onBlur={() => {
          if (!value || value.trim() === "") {
            toast.error("Draft description cannot be empty");
            setValue(descriptionValue.current);
          } else if (descriptionValue.current !== value) {
            updateDraftDescription({
              variables: {
                changelist_id: changelistId,
                changelist: { description: value },
              },
            }).then(() => {
              descriptionValue.current = value;
            });
          }
        }}
      />
      {readOnly && (
        <Tooltip id="read-only-tooltip" title="You cannot edit published or archived drafts">
          <AccentBadge accentColor="accentInfo30" icon={<InfoIcon />} label="Read Only" />
        </Tooltip>
      )}
    </Stack>
  );
};

export const EditorHeader = ({
  description,
  needsMerge,
  flowSummary,
}: {
  description: string;
  needsMerge: boolean;
  flowSummary?: FlowSummary | null;
}) => {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [currentAction, setCurrentAction] = useState<EditorAction>(null);
  const readOnly = useEditorStoreContext((store) => [DraftStatus.RUNNING, DraftStatus.REPLACED].includes(store.status));
  const { urlName } = useAccountContext();

  const { user } = useAuth();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const goBack = (exitPath?: string) => {
    if (exitPath) {
      navigate(exitPath);
    } else {
      const defaultRedirectUrl = `/app/${urlName}/scripts/drafts`;
      const referrer = searchParams.get("referrer");
      const itIsPublished = referrer?.includes("published");
      const redirectionOnCloseUrl = referrer && !itIsPublished ? referrer : defaultRedirectUrl;
      navigate(redirectionOnCloseUrl);
    }
  };

  const hasEdits = useEditorStoreContext((store) =>
    store.files.some(
      (f) => f.editFlags?.created || f.editFlags?.deleted || f.editFlags?.renamed || f.editFlags?.updated
    )
  );

  const setStatus = useEditorStoreContext((store) => store.setStatus);
  const changelistId = useEditorStoreContext((store) => store.id);
  const resetClientFileEdits = useEditorStoreContext((store) => store.resetClientFileDeletions);
  const updateOpenedFilesPaths = useEditorStoreContext((state) => state.updateOpenedFilesPaths);

  const canSeeBetaMessages = hasPermission(Permissions.BetaMessages, user);

  const updateCompilationResult = useEditorStoreContext((store) => store.updateCompilationResult);

  const apolloClient = useApolloClient();
  const refetchOnConflict = useCallback(
    () => apolloClient.refetchQueries({ include: ["query_full_editor_page", "query_compilation_result"] }),
    [apolloClient]
  );

  const [updateChangelist, { loading }] = useMutation<UpdateChangelistMutation, UpdateChangelistMutationVariables>(
    UpdateChangelistDocument,
    {
      onError: (error) => onSaveChangelistError(error, refetchOnConflict),
    }
  );

  const testExports = useEditorStoreContext(selectTestableDeclarations);

  const files = useEditorStoreContext((store) => store.files);

  const onSave = useCallback(async () => {
    setCurrentAction("save");
    const { payload, hasClientOnlyDeletions } = createPayload(files);

    if (payload.files.length > 0) {
      updateChangelist({
        variables: {
          changelist_id: changelistId,
          changelist: payload,
        },
      })
        .then(async (result) => {
          if (result.data?.save_changelist?.__typename === "SaveChangelistOk") {
            const compilationResult = result.data?.save_changelist?.changelist?.compilation_result;
            showToastMessage("save", compilationResult?.messages || [], canSeeBetaMessages);
            resetClientFileEdits();
            updateOpenedFilesPaths();
            if (compilationResult) updateCompilationResult(compilationResult);
          }
        })
        .catch((error) => {
          toast.error("There was a problem saving the Draft.");
          console.error(`Save changelist error: ${JSON.stringify(error)}`);
        })
        .finally(() => {
          setCurrentAction(null);
        });
    } else if (hasClientOnlyDeletions) {
      resetClientFileEdits();
      showPersistSuccess("save");
    } else {
      if (hasEdits) {
        const error = new Error(
          `The user tried to save with changes, but 'There are no changes to Save' toast was shown. The files in the store context are: ${files}`
        );
        Sentry.captureException(error);
      }
      toast.info("There are no changes to Save.");
    }
  }, [
    files,
    updateChangelist,
    changelistId,
    canSeeBetaMessages,
    resetClientFileEdits,
    updateOpenedFilesPaths,
    updateCompilationResult,
    hasEdits,
  ]);

  const onTestInitiated = useCallback(() => {
    setCurrentAction("saveBeforeProof");
    const { payload } = createPayload(files);

    updateChangelist({
      variables: { changelist_id: changelistId, changelist: payload },
    })
      .then((result) => {
        if (result.data?.save_changelist?.__typename === "SaveChangelistOk") {
          const compilationResult = result.data?.save_changelist?.changelist?.compilation_result;
          resetClientFileEdits();
          if (compilationResult) updateCompilationResult(compilationResult);
          const messages = compilationResult?.messages || [];
          const { error } = toErrorsWarnings(messages);
          if (error === 0) setShowTestModal(true);
          else showToastMessage("saveBeforeProof", messages, canSeeBetaMessages);
        }
      })
      .catch((error) => {
        toast.error("There was a problem testing the Draft.");
        console.error(`Proof error: ${JSON.stringify(error)}`);
      })
      .finally(() => {
        setCurrentAction(null);
      });
  }, [changelistId, files, updateChangelist, updateCompilationResult, resetClientFileEdits, canSeeBetaMessages]);

  const onTest = (testExports: string[], withNewDownloads: boolean) => {
    setCurrentAction("proof");
    const { payload } = createPayload(files);
    updateChangelist({
      variables: {
        changelist_id: changelistId,
        ...(payload.files.length > 0 && { changelist: payload }),
        test_exports: testExports,
        fetch_new_downloads: withNewDownloads,
        action: "proof",
      },
    })
      .then((res) => {
        if (res.data?.save_changelist?.__typename === "SaveChangelistOk") {
          resetClientFileEdits();
          const compilationResult = res.data?.save_changelist?.changelist?.compilation_result;
          if (compilationResult) updateCompilationResult(compilationResult);

          if (compilationResult?.messages) showToastMessage("proof", compilationResult.messages, canSeeBetaMessages);
        }
      })
      .catch((error) => {
        toast.error("There was a problem testing the Draft.");
        console.error(`Proof error: ${JSON.stringify(error)}`);
      })
      .finally(() => {
        setCurrentAction(null);
      });
  };
  const onPublish = useCallback(
    (force?: boolean) => {
      setCurrentAction("publish");
      const { payload } = createPayload(files);
      const variables: UpdateChangelistMutationVariables = {
        changelist_id: changelistId,
        changelist: payload.files.length > 0 ? payload : {},
        action: "publish",
      };
      updateChangelist({
        variables: force ? { ...variables, force_publish: true } : variables,
        onError: (error) => onSaveChangelistError(error, refetchOnConflict),
      }).then((res) => {
        if (res.data?.save_changelist?.__typename == "SaveChangelistOk") {
          resetClientFileEdits();
          const compilationResult = res.data?.save_changelist?.changelist?.compilation_result;
          if (compilationResult) updateCompilationResult(compilationResult);

          if (compilationResult?.messages) {
            showToastMessage("publish", compilationResult.messages, canSeeBetaMessages);
            const { error } = toErrorsWarnings(compilationResult.messages ?? []);
            if (error === 0) setStatus(DraftStatus.RUNNING);
          }

          setCurrentAction(null);
        } else if (res.data?.save_changelist?.__typename == "NeedsMergeError") {
          setShowWarningModal(true);
        }
      });
    },
    [
      files,
      changelistId,
      updateChangelist,
      refetchOnConflict,
      resetClientFileEdits,
      updateCompilationResult,
      canSeeBetaMessages,
      setStatus,
    ]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (currentAction) {
        return;
      }
      const charCode = e.key;
      const canDevelopScripts = hasPermission(Permissions.DevelopScripts, user);
      const canPublish = hasPermission(Permissions.PublishScripts, user);

      if ((e.ctrlKey || e.metaKey) && charCode === "s" && canDevelopScripts) {
        e.preventDefault();
        onSave();
      } else if ((e.ctrlKey || e.metaKey) && charCode === "y" && canDevelopScripts) {
        e.preventDefault();
        onTestInitiated();
      } else if ((e.ctrlKey || e.metaKey) && charCode === "Enter" && canPublish) {
        e.preventDefault();
        if (needsMerge) setShowWarningModal(true);
        else onPublish();
      }
    },
    [user, currentAction, onSave, onTestInitiated, needsMerge, onPublish]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
  const [exitPath, setExitPath] = useState("");

  return (
    <>
      <LaunchTestDialog
        exports={testExports}
        open={showTestModal}
        onClose={() => setShowTestModal(false)}
        onTest={onTest}
      />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", pt: 0.5 }}>
        <DraftDescription description={description} />
        <Stack direction="row" spacing={2} sx={{ maxHeight: "90%", alignItems: "center" }}>
          <DocsButton />

          {!readOnly ? (
            <>
              <ViewTestRunsButton
                flowSummary={flowSummary}
                {...(hasEdits
                  ? {
                      onClick: (path) => {
                        setExitPath(path || "");
                        setShowUnsavedChangesModal(true);
                      },
                    }
                  : {})}
              />
              <IfPermission permission={Permissions.DevelopScripts}>
                <Tooltip id="tooltip" title={getTooltip("save")} placement="bottom" arrow>
                  <span>
                    <StatefulButton
                      variant="outlined"
                      size="small"
                      onClick={() => onSave()}
                      loading={(loading && currentAction === "save") || currentAction === "saveBeforeProof"}
                      startIcon={<FloppyDiskIcon />}
                      disabled={loading}
                    >
                      Save
                    </StatefulButton>
                  </span>
                </Tooltip>
                <Tooltip id="tooltip" title={getTooltip("test")} placement="bottom" arrow>
                  <span>
                    <StatefulButton
                      variant="outlined"
                      size="small"
                      onClick={() => onTestInitiated()}
                      loading={loading && currentAction === "proof"}
                      startIcon={<FlaskIcon />}
                      disabled={loading}
                    >
                      Test
                    </StatefulButton>
                  </span>
                </Tooltip>
              </IfPermission>
              <Divider orientation="vertical" flexItem variant="middle" />
              <IfPermission permission={Permissions.PublishScripts}>
                <Tooltip id="tooltip" title={getTooltip("publish")} placement="bottom" arrow>
                  <span>
                    <StatefulButton
                      size="small"
                      onClick={needsMerge ? () => setShowWarningModal(true) : () => onPublish()}
                      loading={loading && currentAction === "publish"}
                      startIcon={<StarIcon />}
                      disabled={loading}
                    >
                      Publish
                    </StatefulButton>
                  </span>
                </Tooltip>
              </IfPermission>
            </>
          ) : null}
          <IconButton
            data-testid="close-editor-btn"
            onClick={hasEdits ? () => setShowUnsavedChangesModal(true) : () => goBack()}
          >
            <XIcon fontSize="large" />
          </IconButton>
        </Stack>
        <MergeWarningDialog
          open={showWarningModal}
          onConfirm={() => {
            onPublish(true);
            setShowWarningModal(false);
          }}
          onClose={() => setShowWarningModal(false)}
        />
        <UnsavedChangesDialog
          open={showUnsavedChangesModal}
          onClose={() => setShowUnsavedChangesModal(false)}
          onConfirm={() => goBack(exitPath)}
          setExitPath={setExitPath}
        />
      </Stack>
    </>
  );
};
